import { useDispatch, useSelector } from "react-redux";

import { can } from "@/app/auth/can";
import type { RootState } from "@/app/store";
import { type AppDispatch } from "@/app/store";
import type { Permission } from "@/shared/types/permission.types";
import { UserRole, UserType } from "@/shared/types/user.entity";

import { setRequires2FA, clearError } from "../store/authSlice";
import { thunkDoLogin, thunkDoLogout, thunkChangePassword } from "../store/authThunks";

export const useAuth = () => {
  const { isAuthenticated, isInitialized, isLoading } = useSelector((state: RootState) => state.auth);

  const dispatch = useDispatch<AppDispatch>();

  const user = useSelector((state: RootState) => state.auth.user);

  const isInternalUser = useSelector((state: RootState) => state.auth.user?.user_type === UserType.INTERNAL);

  const isAdminUser = useSelector((state: RootState) => state.auth.user?.role === UserRole.ADMIN);

  const canAccess = (permission: Permission) => {
    if (!user) return false;

    return can(user, permission);
  };

  const doLogout = () => {
    dispatch(thunkDoLogout());
  };

  const doLogin = (data: { email: string; password: string; twoFactorCode?: string }) => {
    dispatch(
      thunkDoLogin({
        username: data.email,
        password: data.password,
        twoFactorCode: data.twoFactorCode,
      }),
    );
  };

  const requires2FA = useSelector((state: RootState) => state.auth.requires2FA);
  const mustChangePassword = useSelector((state: RootState) => state.auth.mustChangePassword);

  const changeRequires2FA = (requires2FA: boolean) => {
    dispatch(setRequires2FA({ requires2FA }));
  };

  const doChangePassword = (data: { currentPassword: string; newPassword: string }) => {
    dispatch(
      thunkChangePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      }),
    );
  };

  const error = useSelector((state: RootState) => state.auth.error);

  const doClearError = () => {
    dispatch(clearError());
  };

  return {
    isAuthenticated,
    isInitialized,
    isLoading,
    isInternalUser,
    isAdminUser,
    user,
    can: canAccess,
    requires2FA,
    mustChangePassword,
    error,
    doLogout,
    doLogin,
    changeRequires2FA,
    doChangePassword,
    clearError: doClearError,
  };
};
